<script lang="ts">
  import {
    type AgentContext,
    getSessionMessageCount,
    type ThinkingLevel,
  } from "@office-agents/sdk";
  import {
    Check,
    ChevronDown,
    Eye,
    EyeOff,
    FolderOpen,
    MessageSquare,
    Moon,
    Plus,
    Settings,
    Sun,
    Trash2,
    Upload,
  } from "lucide-svelte";
  import { onDestroy, onMount } from "svelte";
  import type { AppAdapter } from "./app-adapter";
  import { ChatController } from "./chat-controller";
  import { setChatContext } from "./chat-runtime-context";
  import ChatInput from "./chat-input.svelte";
  import FilesPanel from "./files-panel.svelte";
  import MessageList from "./message-list.svelte";
  import SettingsPanel from "./settings-panel.svelte";
  import type { ChatTab } from "./types";

  type Theme = "light" | "dark";

  const THEME_KEY = "office-agents-theme";
  const CHATTY_THINKING_LEVEL_KEY = "chatty-thinking-level";
  const CHATTY_JWT_KEY = "chatty-jwt";
  const FOOTER_THINKING_LEVELS: { value: ThinkingLevel; label: string }[] = [
    { value: "low", label: "Low" },
    { value: "medium", label: "Medium" },
    { value: "high", label: "High" },
  ];

  interface Props {
    adapter: AppAdapter;
    context: AgentContext;
  }

  let { adapter, context }: Props = $props();

  const controller = (() => {
    const ctrl = new ChatController(adapter, context);
    setChatContext(ctrl);
    return ctrl;
  })();

  const runtimeState = controller.state;

  let activeTab = $state<ChatTab>("chat");
  let isDragOver = $state(false);
  let dragCounter = $state(0);
  let sessionDropdownOpen = $state(false);
  let sessionDropdownRef = $state<HTMLDivElement | null>(null);
  let modelDisplayName = $state<string | null>(null);
  let loadedModelDisplayKey = "";

  let theme = $state<Theme>(loadTheme());

  function loadTheme(): Theme {
    const saved = localStorage.getItem(THEME_KEY) as Theme | null;
    const initial =
      saved ??
      (window.matchMedia("(prefers-color-scheme: light)").matches
        ? "light"
        : "dark");
    document.documentElement.setAttribute("data-theme", initial);
    return initial;
  }

  function toggleTheme() {
    theme = theme === "dark" ? "light" : "dark";
    document.documentElement.setAttribute("data-theme", theme);
    localStorage.setItem(THEME_KEY, theme);
  }

  function formatTokens(value: number): string {
    if (value >= 1_000_000) return `${(value / 1_000_000).toFixed(1)}M`;
    if (value >= 1_000) return `${(value / 1_000).toFixed(1)}k`;
    return value.toString();
  }

  function formatContextPct(inputTokens: number, contextWindow: number): string {
    if (contextWindow <= 0) return "0%";
    const pct = (inputTokens / contextWindow) * 100;
    if (pct < 1 && pct > 0) return "<1%";
    return `${Math.round(pct)}%`;
  }

  function formatModelLabel(model: string): string {
    if (!model) return "Model";
    return model
      .split(/[\s_-]+/)
      .filter(Boolean)
      .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
      .join(" ");
  }

  function getFooterModelLabel(model: string): string {
    return modelDisplayName || formatModelLabel(model);
  }

  function normalizeChattyApiBaseUrl(value: string): string {
    const trimmed = value.trim();
    try {
      return new URL(trimmed, window.location.origin).toString().replace(/\/$/, "");
    } catch {
      return trimmed.replace(/\/$/, "");
    }
  }

  function getChattyApiBaseUrl(): string {
    const env = (import.meta as unknown as { env?: Record<string, string | undefined> })
      .env;
    return normalizeChattyApiBaseUrl(
      env?.VITE_CHATTY_API_BASE_URL || "https://api.chatty-ai.ai",
    );
  }

  async function refreshModelDisplayName(): Promise<void> {
    const providerConfig = $runtimeState.providerConfig;
    const model = providerConfig?.model;
    const plugin = adapter.chattyPluginName;
    if (!providerConfig || !model || !plugin) {
      modelDisplayName = null;
      return;
    }

    const key = `${plugin}:${model}`;
    if (loadedModelDisplayKey === key) return;
    loadedModelDisplayKey = key;

    const token = localStorage.getItem(CHATTY_JWT_KEY);
    if (!token) return;

    try {
      const response = await fetch(`${getChattyApiBaseUrl()}/models`, {
        headers: {
          Authorization: `Bearer ${token}`,
          "X-Chatty-Plugin": plugin,
        },
      });
      if (!response.ok) return;
      const data = (await response.json()) as {
        models?: {
          slug?: string;
          display_name?: string | null;
          is_default?: boolean;
        }[];
      };
      const models = data.models ?? [];
      const selected = models.find((entry) => entry.slug === model);
      const fallback = models.find((entry) => entry.is_default) ?? models[0];
      const effective = selected ?? fallback;

      if (effective?.slug && effective.slug !== model) {
        loadedModelDisplayKey = `${plugin}:${effective.slug}`;
        controller.setProviderConfig({
          ...providerConfig,
          model: effective.slug,
        });
      }

      modelDisplayName = effective?.display_name?.trim() || null;
    } catch {
      // Fallback remains the safe public slug formatter.
    }
  }

  function normalizeFooterThinkingLevel(value: string): ThinkingLevel {
    return FOOTER_THINKING_LEVELS.some((level) => level.value === value)
      ? (value as ThinkingLevel)
      : "medium";
  }

  function handleThinkingChange(event: Event) {
    if (!$runtimeState.providerConfig) return;
    const selected = normalizeFooterThinkingLevel(
      (event.currentTarget as HTMLSelectElement).value,
    );
    localStorage.setItem(CHATTY_THINKING_LEVEL_KEY, selected);
    controller.setProviderConfig({
      ...$runtimeState.providerConfig,
      thinking: selected,
    });
  }

  async function handleNewSession() {
    await controller.newSession();
    sessionDropdownOpen = false;
    activeTab = "chat";
  }

  async function handleSwitchSession(sessionId: string) {
    await controller.switchSession(sessionId);
    sessionDropdownOpen = false;
    activeTab = "chat";
  }

  function handleClickOutside(event: MouseEvent) {
    if (
      sessionDropdownRef &&
      event.target instanceof Node &&
      !sessionDropdownRef.contains(event.target)
    ) {
      sessionDropdownOpen = false;
    }
  }

  function handleDragEnter(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();
    dragCounter += 1;
    if (event.dataTransfer?.types.includes("Files")) {
      isDragOver = true;
    }
  }

  function handleDragLeave(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();
    dragCounter -= 1;
    if (dragCounter === 0) {
      isDragOver = false;
    }
  }

  function handleDragOver(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();
  }

  function handleDrop(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();
    dragCounter = 0;
    isDragOver = false;

    const files = Array.from(event.dataTransfer?.files ?? []);
    if (files.length > 0) {
      void controller.processFiles(files);
    }
  }

  $effect(() => {
    controller.setAdapter(adapter);
  });

  $effect(() => {
    $runtimeState.providerConfig?.model;
    void refreshModelDisplayName();
  });

  onMount(() => {
    const handleAuthChanged = () => {
      loadedModelDisplayKey = "";
      void refreshModelDisplayName();
    };
    window.addEventListener("chatty-auth-changed", handleAuthChanged);
    void refreshModelDisplayName();
    return () => {
      window.removeEventListener("chatty-auth-changed", handleAuthChanged);
    };
  });

  $effect(() => {
    if (!sessionDropdownOpen) return undefined;
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  });

  onDestroy(() => {
    controller.dispose();
  });

  const currentName = $derived(
    $runtimeState.currentSession?.name ?? "New Chat",
  );
  const truncatedName = $derived(
    currentName.length > 20 ? `${currentName.slice(0, 18)}…` : currentName,
  );
  const followMode = $derived($runtimeState.providerConfig?.followMode ?? true);
  const HeaderExtras = $derived(adapter.HeaderExtras);
  const SelectionIndicator = $derived(adapter.SelectionIndicator);
  const showProviderSettings = $derived(adapter.showProviderSettings ?? true);
</script>

<div
  role="application"
  class="flex flex-col h-full bg-(--chat-bg) relative"
  style="font-family: var(--chat-font-mono)"
  ondragenter={handleDragEnter}
  ondragleave={handleDragLeave}
  ondragover={handleDragOver}
  ondrop={handleDrop}
>
  <div class="border-b border-(--chat-border) bg-(--chat-bg)">
    <div class="flex items-center justify-between px-2">
      <div class="flex">
        {#if activeTab === "chat"}
          <div class="relative" bind:this={sessionDropdownRef}>
            <button
              type="button"
              onclick={() => (sessionDropdownOpen = !sessionDropdownOpen)}
              class="flex items-center gap-1 px-3 py-2 text-xs uppercase tracking-wider border-b-2 border-(--chat-accent) text-(--chat-text-primary) transition-colors"
              style="font-family: var(--chat-font-mono)"
            >
              <MessageSquare size={12} />
              <span class="max-w-[100px] truncate">{truncatedName}</span>
              <ChevronDown
                size={12}
                class={`transition-transform ${sessionDropdownOpen ? "rotate-180" : ""}`}
              />
            </button>

            {#if sessionDropdownOpen}
              <div
                class="absolute top-full left-0 mt-1 w-56 bg-(--chat-bg) border border-(--chat-border) rounded shadow-lg z-50 overflow-hidden"
              >
                <button
                  type="button"
                  onclick={handleNewSession}
                  disabled={$runtimeState.isStreaming}
                  class={`w-full flex items-center gap-2 px-3 py-2 text-xs transition-colors border-b border-(--chat-border) ${$runtimeState.isStreaming ? "text-(--chat-text-muted) cursor-not-allowed" : "text-(--chat-accent) hover:bg-(--chat-bg-secondary)"}`}
                >
                  <Plus size={14} />
                  New Chat
                </button>

                <div class="max-h-48 overflow-y-auto">
                  {#each $runtimeState.sessions as session (session.id)}
                    {@const isCurrent =
                      session.id === $runtimeState.currentSession?.id}
                    {@const isDisabled =
                      $runtimeState.isStreaming && !isCurrent}
                    <button
                      type="button"
                      disabled={isDisabled}
                      class={`flex items-center justify-between px-3 py-2 text-xs transition-colors w-full text-left ${isCurrent ? "bg-(--chat-bg-secondary)" : ""} ${isDisabled ? "cursor-not-allowed opacity-50" : "cursor-pointer hover:bg-(--chat-bg-secondary)"}`}
                      onclick={() => handleSwitchSession(session.id)}
                    >
                      <div class="flex items-center gap-2 min-w-0 flex-1">
                        {#if isCurrent}
                          <Check
                            size={12}
                            class="text-(--chat-accent) shrink-0"
                          />
                        {:else}
                          <div class="w-3 shrink-0"></div>
                        {/if}
                        <span class="truncate text-(--chat-text-primary)">
                          {session.name}
                        </span>
                      </div>
                      <span
                        class="text-[10px] text-(--chat-text-muted) shrink-0 ml-2"
                      >
                        {getSessionMessageCount(session)}
                      </span>
                    </button>
                  {/each}
                </div>

                {#if $runtimeState.sessions.length > 1 && $runtimeState.currentSession}
                  <button
                    type="button"
                    disabled={$runtimeState.isStreaming}
                    onclick={async (event) => {
                      event.stopPropagation();
                      await controller.deleteCurrentSession();
                      sessionDropdownOpen = false;
                    }}
                    class={`w-full flex items-center gap-2 px-3 py-2 text-xs transition-colors border-t border-(--chat-border) ${$runtimeState.isStreaming ? "text-(--chat-text-muted) cursor-not-allowed" : "text-(--chat-error) hover:bg-(--chat-bg-secondary)"}`}
                  >
                    <Trash2 size={14} />
                    Delete Current Session
                  </button>
                {/if}
              </div>
            {/if}
          </div>
        {:else}
          <button
            type="button"
            onclick={() => (activeTab = "chat")}
            class="flex items-center gap-1.5 px-3 py-2 text-xs uppercase tracking-wider border-b-2 border-transparent transition-colors text-(--chat-text-muted) hover:text-(--chat-text-secondary)"
            style="font-family: var(--chat-font-mono)"
          >
            <MessageSquare size={12} />
            Chat
          </button>
        {/if}

        <button
          type="button"
          onclick={() => (activeTab = "files")}
          class={`flex items-center gap-1.5 px-3 py-2 text-xs uppercase tracking-wider border-b-2 transition-colors ${activeTab === "files" ? "border-(--chat-accent) text-(--chat-text-primary)" : "border-transparent text-(--chat-text-muted) hover:text-(--chat-text-secondary)"}`}
          style="font-family: var(--chat-font-mono)"
        >
          <FolderOpen size={12} />
          Files
        </button>

        {#if showProviderSettings}
          <button
            type="button"
            onclick={() => (activeTab = "settings")}
            class={`flex items-center gap-1.5 px-3 py-2 text-xs uppercase tracking-wider border-b-2 transition-colors ${activeTab === "settings" ? "border-(--chat-accent) text-(--chat-text-primary)" : "border-transparent text-(--chat-text-muted) hover:text-(--chat-text-secondary)"}`}
            style="font-family: var(--chat-font-mono)"
          >
            <Settings size={12} />
            Settings
          </button>
        {/if}
      </div>

      <div class="flex items-center">
        {#if activeTab === "chat" && HeaderExtras}
          <HeaderExtras />
        {/if}

        {#if activeTab === "chat" && (adapter.showFollowModeToggle ?? true)}
          <button
            type="button"
            onclick={() => controller.toggleFollowMode()}
            class={`p-1.5 transition-colors ${followMode ? "text-(--chat-accent) hover:text-(--chat-text-primary)" : "text-(--chat-text-muted) hover:text-(--chat-text-primary)"}`}
            data-tooltip={followMode ? "Follow mode: ON" : "Follow mode: OFF"}
          >
            {#if followMode}
              <Eye size={14} />
            {:else}
              <EyeOff size={14} />
            {/if}
          </button>
        {/if}

        <button
          type="button"
          onclick={toggleTheme}
          class="p-1.5 text-(--chat-text-muted) hover:text-(--chat-text-primary) transition-colors"
          data-tooltip={theme === "dark" ? "Light mode" : "Dark mode"}
        >
          {#if theme === "dark"}
            <Sun size={14} />
          {:else}
            <Moon size={14} />
          {/if}
        </button>

        {#if activeTab === "chat" && $runtimeState.messages.length > 0}
          <button
            type="button"
            onclick={() => controller.clearMessages()}
            class="p-1.5 text-(--chat-text-muted) hover:text-(--chat-error) transition-colors"
            data-tooltip="Clear messages"
          >
            <Trash2 size={14} />
          </button>
        {/if}
      </div>
    </div>
  </div>

  {#if activeTab === "chat"}
    <MessageList />
    {#if SelectionIndicator}
      <SelectionIndicator />
    {/if}
    <ChatInput />
    {#if $runtimeState.providerConfig}
      <div
        class="flex items-center justify-between px-3 py-1.5 text-[10px] border-t border-(--chat-border) bg-(--chat-bg-secondary) text-(--chat-text-muted)"
        style="font-family: var(--chat-font-mono)"
      >
        <div
          class="min-w-0 truncate"
          title="Usage shows input and output tokens for this chat"
          aria-label="Usage token summary"
        >
          Usage: {formatTokens($runtimeState.sessionStats.inputTokens)} in · {formatTokens(
            $runtimeState.sessionStats.outputTokens,
          )} out{#if $runtimeState.sessionStats.contextWindow > 0}
            {" "}<span
              title="How much of the available conversation context was used in the last request"
            >
              · Context {formatContextPct(
                $runtimeState.sessionStats.lastInputTokens || 0,
                $runtimeState.sessionStats.contextWindow,
              )}
            </span>
          {/if}
        </div>
        <div class="flex items-center gap-2 min-w-0 shrink-0">
          <span
            class="text-(--chat-text-secondary) truncate max-w-[120px]"
            title={getFooterModelLabel($runtimeState.providerConfig.model)}
          >
            {getFooterModelLabel($runtimeState.providerConfig.model)}
          </span>
          <span class="text-(--chat-text-muted)" aria-hidden="true">|</span>
          <label class="flex items-center gap-1 text-(--chat-text-muted) shrink-0">
            <span>Thinking</span>
            <select
              aria-label="Thinking level"
              title="Choose response thinking level"
              value={normalizeFooterThinkingLevel(
                $runtimeState.providerConfig.thinking,
              )}
              onchange={handleThinkingChange}
              disabled={$runtimeState.isStreaming}
              class="bg-(--chat-input-bg) text-(--chat-text-secondary) border border-(--chat-border) rounded px-1.5 py-0.5 text-[10px] focus:outline-none focus:border-(--chat-border-active) disabled:opacity-50"
              style="font-family: var(--chat-font-mono)"
            >
              {#each FOOTER_THINKING_LEVELS as level (level.value)}
                <option value={level.value}>{level.label}</option>
              {/each}
            </select>
          </label>
        </div>
      </div>
    {/if}
  {:else if activeTab === "files"}
    <FilesPanel />
  {:else if activeTab === "settings" && showProviderSettings}
    <SettingsPanel />
  {:else}
    <MessageList />
    {#if SelectionIndicator}
      <SelectionIndicator />
    {/if}
    <ChatInput />
  {/if}

  {#if isDragOver}
    <div
      class="absolute inset-0 z-50 flex items-center justify-center bg-(--chat-bg)/80 backdrop-blur-sm"
    >
      <div
        class="flex flex-col items-center gap-3 p-8 border-2 border-dashed border-(--chat-accent) rounded-lg"
      >
        <Upload size={32} class="text-(--chat-accent)" />
        <span class="text-sm text-(--chat-text-primary)">Drop files here</span>
      </div>
    </div>
  {/if}
</div>
